import React from 'react';
import { Calendar, MapPin, Clock, Users, Bell } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { FadeIn } from '../components/ui/FadeIn';
import { Link } from 'react-router-dom';
export function EventsPage() {
  // Set to true when you have upcoming events to display
  const hasUpcomingEvents = false;
  const upcomingEvents = [
    // Example event structure - uncomment and populate when ready:
    // {
    //   title: "Annual Gala Dinner 2024",
    //   date: "Nov 15, 2024",
    //   time: "18:00 - 23:00",
    //   location: "Marriott Hotel, London",
    //   image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    //   desc: "Join us for an evening of networking, fine dining, and fundraising for our latest charity initiatives."
    // }
  ];
  const pastEvents = [{
    title: 'Summer Family Picnic 2023',
    date: 'Aug 20, 2023',
    location: 'Hyde Park, London',
    attendees: '50+ families'
  }, {
    title: 'Medical Ethics Seminar',
    date: 'Jun 10, 2023',
    location: 'Virtual (Zoom)',
    attendees: '120+ participants'
  }, {
    title: 'Flood Relief Fundraiser',
    date: 'Jan 15, 2023',
    location: 'Manchester Conference Centre',
    attendees: '200+ attendees'
  }, {
    title: 'Annual Gala Dinner 2022',
    date: 'Nov 12, 2022',
    location: 'Hilton Hotel, Birmingham',
    attendees: '150+ guests'
  }];
  return <>
      <SEO title="Events" description="Upcoming events, seminars, and gatherings organized by KMA UK. Join us for networking, education, and community engagement." data-id="element-370" />

      <div className="bg-primary-900 text-white py-20" data-id="element-371">
        <div className="container mx-auto px-4 text-center" data-id="element-372">
          <FadeIn data-id="element-373">
            <Calendar className="h-12 w-12 mx-auto mb-6 text-primary-200" data-id="element-374" />
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6" data-id="element-375">
              Events & Activities
            </h1>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto" data-id="element-376">
              Connect with colleagues, expand your knowledge, and engage with
              the KMA UK community.
            </p>
          </FadeIn>
        </div>
      </div>

      {/* Upcoming Events Section */}
      <Section data-id="element-377">
        <div className="text-center mb-12" data-id="element-378">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4" data-id="element-379">
            Upcoming Events
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-380">
            Stay connected with our community through professional seminars,
            social gatherings, and charitable initiatives.
          </p>
        </div>

        {!hasUpcomingEvents ? <FadeIn data-id="element-381">
            <Card className="max-w-3xl mx-auto border-2 border-primary-100 bg-primary-50/30" data-id="element-382">
              <CardContent className="p-12 text-center" data-id="element-383">
                <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mb-6 mx-auto" data-id="element-384">
                  <Bell className="h-8 w-8" data-id="element-385" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4" data-id="element-386">
                  Upcoming Events Will Be Announced Soon
                </h3>
                <p className="text-gray-600 mb-6 text-lg" data-id="element-387">
                  Members will be notified first about new events, seminars, and
                  gatherings.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-388">
                  <Link to="/membership" data-id="element-389">
                    <Button size="lg" data-id="element-390">
                      <Users className="mr-2 h-5 w-5" data-id="element-391" />
                      Become a Member
                    </Button>
                  </Link>
                  <Link to="/contact" data-id="element-392">
                    <Button variant="outline" size="lg" data-id="element-393">
                      Contact Us for Updates
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </FadeIn> : <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-394">
            {upcomingEvents.map((event, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-395">
                <Card className="flex flex-col h-full overflow-hidden hover:shadow-xl transition-shadow border-2 border-transparent hover:border-primary-200" data-id="element-396">
                  <div className="h-64 w-full relative overflow-hidden" data-id="element-397">
                    <img src={event.image} alt={event.title} className="w-full h-full object-cover" data-id="element-398" />
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-4 py-2 rounded-lg shadow-md" data-id="element-399">
                      <div className="text-sm font-bold text-primary-800" data-id="element-400">
                        {event.date}
                      </div>
                    </div>
                  </div>
                  <CardContent className="flex-1 p-6 flex flex-col" data-id="element-401">
                    <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4" data-id="element-402">
                      {event.title}
                    </h3>
                    <div className="space-y-2 text-sm text-gray-600 mb-4 flex-1" data-id="element-403">
                      <div className="flex items-center" data-id="element-404">
                        <Clock className="h-4 w-4 mr-2 text-accent-600" data-id="element-405" />
                        {event.time}
                      </div>
                      <div className="flex items-center" data-id="element-406">
                        <MapPin className="h-4 w-4 mr-2 text-accent-600" data-id="element-407" />
                        {event.location}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-6" data-id="element-408">{event.desc}</p>
                    <Button className="w-full" data-id="element-409">Register Now</Button>
                  </CardContent>
                </Card>
              </FadeIn>)}
          </div>}
      </Section>

      {/* Past Events */}
      <Section background="gray" data-id="element-410">
        <div className="text-center mb-12" data-id="element-411">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4" data-id="element-412">
            Past Events
          </h2>
          <p className="text-gray-600" data-id="element-413">
            A look back at our recent gatherings and community activities.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-414">
          {pastEvents.map((event, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-415">
              <Card className="h-full hover:shadow-md transition-shadow" data-id="element-416">
                <CardHeader className="pb-3" data-id="element-417">
                  <CardTitle className="text-lg leading-tight" data-id="element-418">
                    {event.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2" data-id="element-419">
                  <div className="flex items-center text-sm text-gray-600" data-id="element-420">
                    <Calendar className="h-4 w-4 mr-2 text-primary-600" data-id="element-421" />
                    {event.date}
                  </div>
                  <div className="flex items-center text-sm text-gray-600" data-id="element-422">
                    <MapPin className="h-4 w-4 mr-2 text-primary-600" data-id="element-423" />
                    {event.location}
                  </div>
                  <div className="flex items-center text-sm text-gray-600" data-id="element-424">
                    <Users className="h-4 w-4 mr-2 text-primary-600" data-id="element-425" />
                    {event.attendees}
                  </div>
                </CardContent>
              </Card>
            </FadeIn>)}
        </div>
      </Section>

      {/* Member Benefits CTA */}
      <Section className="text-center" data-id="element-426">
        <FadeIn data-id="element-427">
          <div className="max-w-3xl mx-auto" data-id="element-428">
            <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4" data-id="element-429">
              Don't Miss Out on Future Events
            </h2>
            <p className="text-gray-600 mb-8 text-lg" data-id="element-430">
              KMA UK members receive priority notification and exclusive access
              to all our events, seminars, and networking opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-431">
              <Link to="/membership" data-id="element-432">
                <Button size="lg" data-id="element-433">Join KMA UK Today</Button>
              </Link>
              <Link to="/about" data-id="element-434">
                <Button variant="outline" size="lg" data-id="element-435">
                  Learn More About Us
                </Button>
              </Link>
            </div>
          </div>
        </FadeIn>
      </Section>
    </>;
}